import { createContext, useContext, useState, useCallback, type ReactNode, type ChangeEvent } from "react"

export interface ChatInputContextType {
  input: string
  setInput: (input: string) => void
  handleInputChange: (e: ChangeEvent<HTMLTextAreaElement>) => void
  resetInput: () => void
  isInputEmpty: boolean
}

const ChatInputContext = createContext<ChatInputContextType | undefined>(undefined)

interface ChatInputProviderProps {
  children: ReactNode
  initialInput?: string
}

export function ChatInputProvider({ 
  children, 
  initialInput = "" 
}: ChatInputProviderProps) {
  const [input, setInput] = useState(initialInput)

  const handleInputChange = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value)
  }, [])

  const resetInput = useCallback(() => {
    setInput("")
  }, [])

  const isInputEmpty = input.trim().length === 0

  const value: ChatInputContextType = {
    input,
    setInput,
    handleInputChange,
    resetInput,
    isInputEmpty
  }

  return (
    <ChatInputContext.Provider value={value}>
      {children}
    </ChatInputContext.Provider>
  )
}

export function useChatInput() {
  const context = useContext(ChatInputContext)
  if (context === undefined) {
    throw new Error("useChatInput must be used within a ChatInputProvider")
  }
  return context
} 